import * as THREE from "three";
import { LevelSim, SimEvent, SimEventKind, ZoneKind } from "@highmarch/domain";
import type { VFX } from "../../../../art/vfx.js";
import { audio } from "../audio/audio";
import type { AudioCue } from "../audio/audio";
import { Tier4Creatures } from "./views/tier4Creatures";
import { groundAt, roadLevelAt } from "./terrain";

/**
 * Tier IV on the battlefield (M21, docs/TOWERS_V2.md "Tier 4"): what the sim's tier-4 events and state look and sound
 * like, built from the shared sprite VFX (art/vfx.js) and the existing cues — no new assets.
 *  - The phoenix, the spiders and Polymorph's sheep (views/tier4Creatures.ts).
 *  - Ground zones (`sim.zones`): thorns (green-brown burrs and a ring), fire (flames), plague (green bubbles and haze),
 *    the quake (dust and a crack), napalm (low flames), each a decal the zone's size for its life, topped up while it lasts.
 *  - The Blizzard Shrine's aura: an icy band of motes at its edge and snow inside; stunned raiders wear a ring of
 *    sparks over their heads; the Tesla Coil glows brighter as it charges and its discharge forks to every victim.
 *  - Each skill's moment (SkillFired, EnemyExecuted, FortressHealed …): a flash, a ring, a number, and its cue.
 * `onTick` after each sim tick (its events), `update` every frame.
 */

const ICE: [number, number, number] = [0.6, 0.85, 1.0];
const GOLD: [number, number, number] = [1.0, 0.82, 0.3];
const ZONE_LOOK: Readonly<Record<ZoneKind, { color: [number, number, number]; decal: "ring" | "scorch" | "poison" | "crack" | "rune" }>> = {
  [ZoneKind.Thorns]: { color: [0.55, 0.75, 0.25], decal: "rune" },
  [ZoneKind.Firestorm]: { color: [1.0, 0.45, 0.12], decal: "scorch" },
  [ZoneKind.PlagueCloud]: { color: [0.45, 0.95, 0.3], decal: "poison" },
  [ZoneKind.Earthquake]: { color: [0.62, 0.5, 0.36], decal: "crack" },
  [ZoneKind.Napalm]: { color: [1.0, 0.36, 0.08], decal: "scorch" },
};
/** Seconds between a zone's top-up particles, and between a stunned raider's spark rings. */
const ZONE_EVERY = 0.22, STUN_EVERY = 0.35;
/** The cue each skill's moment plays (by skill id; the rest are quiet). */
const SKILL_CUE: Readonly<Record<string, AudioCue>> = {
  shockwave: "mortarImpact", thunderclap: "fire_lightning", clusterBomb: "fire_cannon", overheat: "fire_fire", volley: "fire_ballista",
  rallyCry: "fire_beacon", emberNova: "fire_fire", rebirth: "fire_fire", polymorph: "hexCurse", banish: "hexCurse", headshot: "deathBig",
  annihilate: "deathBig", doomMark: "mortarImpact", contagion: "fire_poison", impale: "fire_ballista", deepFreeze: "fire_frost",
  webSnare: "fire_poison", ambush: "fire_archer", rangerVolley: "fire_archer",
};

export class Tier4Fx {
  readonly group = new THREE.Group();
  readonly creatures: Tier4Creatures;
  fx: () => VFX | null = () => null;
  /** Set by the owner: a tower part's world position, an enemy's drawn feet and body centre. */
  towerNode: (towerId: number, part: string) => THREE.Vector3 | null = () => null;
  enemyFeet: (id: number) => THREE.Vector3 | null = () => null;
  enemyBody: (id: number) => THREE.Vector3 | null = () => null;
  private time = 0;
  private zoneIn = 0;
  private stunIn = 0;
  /** Zones whose decal is down (by id). */
  private readonly decals = new Set<number>();

  constructor(private readonly sim: LevelSim, private readonly reduced: () => boolean) {
    this.creatures = new Tier4Creatures(sim);
    this.group.add(this.creatures.group);
  }

  onTick(): void {
    if (!this.sim.ascension.active && this.sim.zones.length === 0) return;
    this.creatures.onTick();
    const evs = this.sim.events.all();
    for (let i = 0; i < evs.length; i++) this.event(evs[i], evs, i);
  }

  private event(ev: SimEvent, evs: readonly SimEvent[], i: number): void {
    const fx = this.fx();
    switch (ev.kind) {
      case SimEventKind.BranchChosen: audio.play("towerUpgradeMax"); return;
      case SimEventKind.SkillLearned: audio.play("towerUpgrade"); this.towerBurst(this.sim.towerSlots.filter((t) => t.alive && t.archetype === ev.a && t.tier >= 4).map((t) => t.id), GOLD); return;
      case SimEventKind.SkillFired: this.skill(ev.a, ev.b, ev.c); return;
      case SimEventKind.ZoneCreated: { const z = this.sim.zones.find((x) => x.id === ev.a); if (z && fx) this.zoneDecal(fx, z.id, z.kind, z.x.toNumber(), z.y.toNumber(), z.radius.toNumber(), z.ticks); return; }
      case SimEventKind.TeslaDischarged: {
        audio.play("baseStrike");
        if (!fx) return;
        const from = this.towerNode(ev.a, "crystal") ?? this.towerTop(ev.a);
        const hits: THREE.Vector3[] = [];
        const first = this.enemyBody(ev.b); if (first) hits.push(first);
        for (let k = i + 1; k < evs.length && evs[k].kind !== SimEventKind.TeslaDischarged; k++) if (evs[k].kind === SimEventKind.ChainHit && evs[k].b === ev.a) { const p = this.enemyBody(evs[k].a); if (p) hits.push(p); }
        if (from) fx.teslaFire(from, hits, { scale: 1.6 });
        for (const h of hits) fx.teslaImpact(h, 1.3);
        return;
      }
      case SimEventKind.PhoenixRisen: { audio.play("fire_fire"); const p = this.sim.phoenixes.find((x) => x.towerId === ev.a); if (p && fx) this.blaze(fx, p.x.toNumber(), p.y.toNumber(), ev.b === 1 ? 1.4 : 0.8); return; }
      case SimEventKind.PhoenixFell: { const p = this.sim.phoenixes.find((x) => x.towerId === ev.a); if (p && fx) { const at = this.air(p.x.toNumber(), p.y.toNumber()); fx.embers(at, 14, { radius: 0.4, life: 0.9 }); fx.fireSmoke(at, 4, { size: 0.4 }); } return; }
      case SimEventKind.SpiderLatched: { audio.play("fire_poison"); const p = this.enemyBody(ev.b); if (p && fx) fx.bubbles(p, 5, { color: [0.45, 1, 0.3], radius: 0.18 }); return; }
      case SimEventKind.EnemyPolymorphed: { const p = this.enemyBody(ev.a); if (p && fx) { fx.sparkles(p, 16, { color: [1, 0.6, 0.95], radius: 0.35 }); fx.flash(p, 0.6, [1, 0.7, 1]); fx.smoke(p, 4, { size: 0.35, color: [1, 1, 1] }); } return; }
      case SimEventKind.EnemyBanished: { const p = this.enemyBody(ev.a); if (p && fx) { fx.ring(p, { size: 0.9, color: [0.7, 0.4, 1], life: 0.5 }); fx.sparkles(p, 12, { color: [0.75, 0.45, 1], radius: 0.3 }); } return; }
      case SimEventKind.EnemyExecuted: { const p = this.enemyBody(ev.a); if (p && fx) { fx.flash(p, 0.9, [1, 0.95, 0.8]); fx.number(p.clone().setY(p.y + 0.4), ev.c >= 0 && this.skillId(ev.c) === "headshot" ? "HEADSHOT" : "OBLITERATED", { color: [1, 0.85, 0.35], size: 0.32 }); } return; }
      case SimEventKind.FortressHealed: { audio.play("eventProgress"); const base = this.sim.baseTower; const at = new THREE.Vector3(base.cell.x, groundAt(base.cell.x, base.cell.y) + 1.2, base.cell.y); if (fx) { fx.sparkles(at, 18, { color: [0.6, 1, 0.75], radius: 0.6, rise: 0.8 }); fx.number(at.clone().setY(at.y + 0.6), `+${ev.a}`, { color: [0.6, 1, 0.7], size: 0.36 }); } return; }
      default: return;
    }
  }

  /** A skill's moment at its tower and/or the enemy it centred on. */
  private skill(towerId: number, skillIndex: number, enemyId: number): void {
    const id = this.skillId(skillIndex);
    const cue = SKILL_CUE[id];
    if (cue) audio.play(cue);
    const fx = this.fx();
    if (!fx) return;
    const at = enemyId !== 0 ? this.enemyBody(enemyId) : null;
    const top = this.towerTop(towerId);
    switch (id) {
      case "shockwave": case "thunderclap": if (top) fx.ring(top.clone().setY(groundAt(top.x, top.z) + 0.1), { size: 2.2, color: id === "shockwave" ? [1, 0.85, 0.5] : [0.75, 0.6, 1], life: 0.45, flat: true }); return;
      case "clusterBomb": if (at) fx.cannonBlast(at); return;
      case "overheat": if (top) { fx.flames(top, { size: 0.5, life: 0.8 }); fx.glowPulse(top, { size: 1.0, color: [1, 0.45, 0.1], life: 0.8 }); } return;
      case "volley": case "rangerVolley": if (top) fx.sparks(top, 10, { color: GOLD, up: 1.2 }); return;
      case "rallyCry": if (top) { fx.ring(top.clone().setY(groundAt(top.x, top.z) + 0.1), { size: 4.5, color: [1, 0.3, 0.25], life: 0.7, flat: true }); fx.glowPulse(top, { size: 1.4, color: [1, 0.35, 0.25], life: 0.6 }); } return;
      case "emberNova": { const p = this.sim.phoenixes.find((x) => x.towerId === towerId); if (p) this.blaze(fx, p.x.toNumber(), p.y.toNumber(), 1.5); return; }
      case "rebirth": return;                                          // PhoenixRisen draws its blaze
      case "doomMark": if (at) { fx.mortarImpact(at, { color: [0.7, 0.3, 1], trauma: 0.05 }); } return;
      case "contagion": if (at) fx.bubbles(at, 10, { color: [0.45, 1, 0.3], radius: 0.5 }); return;
      case "impale": case "ambush": case "webSnare": case "deepFreeze": if (at) fx.ring(at, { size: 0.55, color: id === "deepFreeze" ? ICE : id === "webSnare" ? [0.95, 0.95, 0.95] : GOLD, life: 0.35 }); return;
      default: if (at) fx.flash(at, 0.4, GOLD); return;
    }
  }

  private skillId(i: number): string { return this.sim.balance.branches.skills[i]?.id ?? ""; }

  private towerTop(id: number): THREE.Vector3 | null {
    const t = this.sim.findTower(id);
    return t ? new THREE.Vector3(t.cell.x, groundAt(t.cell.x, t.cell.y) + 1.1, t.cell.y) : null;
  }
  private towerBurst(ids: readonly number[], color: [number, number, number]): void {
    const fx = this.fx();
    if (!fx) return;
    for (const id of ids) { const top = this.towerTop(id); if (top) { fx.sparkles(top, 20, { color, radius: 0.5, rise: 1 }); fx.glowPulse(top, { size: 1.2, color, life: 0.7 }); } }
  }
  private air(x: number, z: number): THREE.Vector3 { return new THREE.Vector3(x, groundAt(x, z) + 1.15, z); }
  private blaze(fx: VFX, x: number, z: number, size: number): void {
    const at = this.air(x, z);
    fx.flash(at, size, [1, 0.6, 0.2]);
    fx.ring(new THREE.Vector3(x, roadLevelAt(x, z) + 0.08, z), { size: size * 1.8, color: [1, 0.5, 0.15], life: 0.5, flat: true });
    fx.embers(at, 18, { radius: size * 0.6, life: 0.8 });
  }

  private zoneDecal(fx: VFX, id: number, kind: ZoneKind, x: number, z: number, radius: number, ticks: number): void {
    const look = ZONE_LOOK[kind];
    const life = ticks / 20;
    fx.decal(new THREE.Vector3(x, roadLevelAt(x, z) + 0.02, z), { type: look.decal, color: look.color, size: radius * 2.1, life, hold: life * 0.7, alpha: 0.85 });
    if (kind === ZoneKind.Earthquake) fx.dust(new THREE.Vector3(x, roadLevelAt(x, z) + 0.1, z), 14, { size: 0.5, color: look.color });
    this.decals.add(id);
  }

  /** Per frame: the creatures, the zones' top-ups, the Blizzard auras, the stunned raiders' sparks, the Tesla's charge. */
  update(alpha: number, dt: number): void {
    this.time += dt;
    this.creatures.update(alpha, dt);
    const fx = this.fx();
    if (!fx || this.reduced()) return;
    this.zoneIn -= dt; this.stunIn -= dt;
    if (this.zoneIn <= 0) {
      this.zoneIn = ZONE_EVERY;
      for (const z of this.sim.zones) {
        const x = z.x.toNumber(), y = z.y.toNumber(), r = z.radius.toNumber();
        const a = Math.random() * Math.PI * 2, d = Math.sqrt(Math.random()) * r * 0.8;
        const p = new THREE.Vector3(x + Math.cos(a) * d, roadLevelAt(x, y) + 0.06, y + Math.sin(a) * d);
        const c = ZONE_LOOK[z.kind].color;
        if (z.kind === ZoneKind.Firestorm || z.kind === ZoneKind.Napalm) fx.flames(p, { size: 0.28, life: 0.5 });
        else if (z.kind === ZoneKind.PlagueCloud) { fx.bubbles(p, 2, { color: c, radius: 0.2 }); fx.smoke(p, 1, { size: r * 0.9, color: c, alpha: 0.25, rise: 0.1 }); }
        else if (z.kind === ZoneKind.Thorns) fx.sparks(p, 2, { color: c, up: 0.4, life: 0.35 });
        else fx.dust(p, 2, { size: 0.3, color: c });
      }
    }
    for (const t of this.sim.towerSlots) {
      if (!t.alive || t.tier < 4) continue;
      const i = this.sim.ascension.branchOf[t.archetype], b = i >= 0 ? this.sim.balance.branches.branches[i] : null;
      if (b?.id === "blizzard" && t.disabledTicksLeft <= 0) {
        const at = new THREE.Vector3(t.cell.x, roadLevelAt(t.cell.x, t.cell.y), t.cell.y);
        const radius = this.sim.rangeOf(t).toNumber();
        fx.auraRing(at, radius, roadLevelAt, { count: Math.round((2 * Math.PI * radius) / 0.3), time: this.time, spin: 0.12, alpha: 0.7, size: 0.18, thickness: 0.06, color: ICE });
        if (Math.random() < dt * 6) { const a = Math.random() * Math.PI * 2, d = Math.random() * radius; fx.sparkles(new THREE.Vector3(t.cell.x + Math.cos(a) * d, groundAt(t.cell.x, t.cell.y) + 1.2, t.cell.y + Math.sin(a) * d), 1, { color: [0.95, 0.98, 1], radius: 0.1, rise: -0.6, life: 1.2 }); }
      } else if (b?.id === "sorcerer" && t.extraBeams.length > 0) {
        // the split beams (the main one is views/arcaneBeams.ts'): thin magenta threads to each extra target
        const from = this.towerNode(t.id, "crystal") ?? this.towerTop(t.id);
        for (const id of t.extraBeams) { const to = id !== 0 ? this.enemyBody(id) : null; if (from && to) fx.beam(from, to, { life: 0.05, width: 0.07, color: 0xff5ad6 }); }
      } else if (b?.id === "tesla" && t.charge > 0 && Math.random() < dt * 4) {
        const top = this.towerNode(t.id, "crystal") ?? this.towerTop(t.id);
        const full = b.tier4.int("chargeTicks", 1);
        if (top) fx.glowPulse(top, { size: 0.4 + 0.8 * Math.min(1, t.charge / full), color: [0.7, 0.6, 1], life: 0.3 });
      }
    }
    if (this.stunIn <= 0) {
      this.stunIn = STUN_EVERY;
      for (const e of this.sim.enemySlots) {
        if (!e.alive || e.stunTicksLeft <= 0) continue;
        const p = this.enemyBody(e.id);
        if (p) fx.sparkles(p.clone().setY(p.y + 0.35), 3, { color: [1, 0.95, 0.5], radius: 0.16, rise: 0.05, life: 0.4 });
      }
    }
  }

  dispose(): void { this.creatures.dispose(); this.group.clear(); this.decals.clear(); }
}
